import React, { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { ChainExplorer } from "@/components/chain/ChainExplorer";
import { PageHero } from "@/components/site/PageHero";
import { BookButton } from "@/components/site/BookButton";
import { Seo } from "@/components/site/Seo";
import { ClinicalProcess } from "@/components/site/ClinicalProcess";

export default function Approach() {
  const [approach, setApproach] = useState(null);
  useEffect(() => {
    api.get("/content/approach").then((r) => setApproach(r.data)).catch(() => {});
  }, []);
  if (!approach) return <div className="h-screen" />;

  return (
    <div>
      <Seo title="Our Approach — Treat the Chain | Psalm 30:2 Chiropractic" description="The kinetic chain philosophy behind Psalm 30:2 Chiropractic in Gardner, Kansas." />
      <PageHero eyebrow={approach.eyebrow} title={approach.headline} intro={approach.intro} />

      <section className="bg-linen py-20 lg:py-28 px-6 md:px-10 lg:px-14" data-testid="approach-chain">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <div className="overline mb-5">Interactive Body Map</div>
            <h2 className="font-serif font-light text-4xl lg:text-5xl text-espresso mb-14 max-w-2xl">
              One nervous system. One connected chain.
            </h2>
          </Reveal>
          <ChainExplorer />
        </div>
      </section>

      <section className="bg-ivory py-24 lg:py-32 px-6 md:px-10 lg:px-14" data-testid="approach-nervous">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <div className="overline mb-6">Chain → Nervous System</div>
            <h2 className="font-serif font-light text-4xl lg:text-5xl leading-[1.05] text-espresso mb-6">Every link feeds your nervous system.</h2>
            <p className="text-lg font-light leading-relaxed text-espressosoft/85 max-w-xl mb-4">The chain isn't just mechanical. Each joint, muscle and fascial line sends a constant stream of information up to your brain (bottom-up), while your brain sends control back down (top-down). Restore motion along the chain and you sharpen that map — improving posture and helping the body stay pain-free.</p>
            <Link to="/nervous-system" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-espresso link-underline" data-testid="approach-nervous-link">Explore the nervous-system science <ArrowRight size={15} /></Link>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-linen border border-espresso/10 rounded-sm p-6 text-center">
                <div className="font-serif text-5xl text-brass">↑</div>
                <div className="font-serif text-2xl text-espresso mt-2">Bottom-up</div>
                <p className="text-xs text-taupe mt-2">Joints & fascia inform the brain</p>
              </div>
              <div className="bg-espresso text-ivory rounded-sm p-6 text-center">
                <div className="font-serif text-5xl text-brass">↓</div>
                <div className="font-serif text-2xl mt-2">Top-down</div>
                <p className="text-xs text-ivory/50 mt-2">Brain controls tone & posture</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <ClinicalProcess bg="bg-stone" testid="approach-clinical-process" />

      <section className="bg-espresso text-ivory py-20 px-6 text-center" data-testid="approach-flow">
        <Reveal>
          <h2 className="font-serif font-light text-4xl lg:text-5xl mb-8">Find the link that's holding you back.</h2>
          <BookButton label="Start Your Assessment" variant="brass" track_label="approach" testid="approach-book-button" />
        </Reveal>
      </section>
    </div>
  );
}
